import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router";
import { NgbHighlight, NgbTypeahead } from "@ng-bootstrap/ng-bootstrap/typeahead";
import { debounceTime, distinctUntilChanged, map, Observable } from "rxjs";

import { ApiService } from "src/app/api.service";
import { APIResults, AssetRoutable } from "src/app/sharedinterfaces";

@Component({
	selector: "app-manage-all-assets",
	templateUrl: "./manage-all-assets.component.html",
	styleUrls: ["./manage-all-assets.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [RouterLink, RouterLinkActive, RouterOutlet, NgbTypeahead, NgbHighlight]
})
export class ManageAllAssetsComponent implements OnInit, OnDestroy {
	private api = inject(ApiService);
	private router = inject(Router);
	private route = inject(ActivatedRoute);

	public treenodes: APIResults.Organization[];
	public searchText = "";
	private searchables = [];

	ngOnInit() {
		this.UpdateTree();
	}

	ngOnDestroy() {
		this.updateTreeSubscription?.unsubscribe();
	}

	private scrollNodeIntoView(el: Element) {
		// This is a workaround for the fact that some browsers (Safari) don't support the scroll container option.
		// We want to use the scroll container option if it's supported, because it will scroll the element into view within the scrollable treeview, rather than scrolling the entire page.
		let supportsContainer = false;
		try { document.createElement("div").scrollIntoView({ get container() { supportsContainer = true; return "nearest"; } } as any); } catch { /* detection failed */ }
		el.scrollIntoView(supportsContainer
			? { behavior: "smooth", block: "start", container: "nearest" } as any
			: { behavior: "smooth", block: "nearest" });
	}

	public async UpdateTree() {
		this.treenodes = await this.api.GetFullTree();
		// Generate searchable entries
		this.searchables = [];
		for (const org of this.treenodes) {
			this.searchables.push({ category: "Org", org: org.organization_id, id: org.organization_id, url: "/admin/assets/org/" + org.organization_id, name: org.name });
			for (const fleet of org.fleets) {
				this.searchables.push({ category: "Fleet", org: org.organization_id, id: org.organization_id + ":" + fleet.fleet_id, url: "/admin/assets/org/" + org.organization_id + "/fleet/" + fleet.fleet_id, name: fleet.fleet_name });
				if (fleet.assets) {
					for (const asset of fleet.assets) {
						this.searchables.push({ category: "Asset", org: org.organization_id, id: org.organization_id + ":" + fleet.fleet_id + ":" + asset.asset_id, url: "/admin/assets/org/" + org.organization_id + "/fleet/" + fleet.fleet_id + "/asset/" + asset.asset_id, name: asset.asset_name, serial: asset.asset_serial_number, obcu: asset.obcu_serial_number });
					}
				}
			}
		}
		setTimeout(() => {
			const orgid = this.route.snapshot.firstChild.params['orgid'];
			if (orgid) {
				const el = document.querySelector(`div[data-orgid='${orgid}']`);
				if (el) {
					el.classList.add("expanded");
					this.scrollNodeIntoView(el);
				}
			}
		}, 0);
	}

	/// Tree event handlers
	public async ClickExpander($event: any) {
		$event.target.parentElement.classList.toggle("expanded");
	}

	public async ClickOrg($event: any) {
		$event.target.parentElement.classList.add("expanded");
	}

	public async StartAssetDrag($event, organization_id, asset_id, asset_name) {
		$event.dataTransfer.setData("asset_id", JSON.stringify({ organization_id, asset_id, asset_name }));
	}

	/** Preserves any child routes if we are going asset to asset */
	public getAssetRouterLink(organization_id: number, fleet_id: number, asset_id: any) {
		const url = this.router.url || "";
		const m = url.match(/\/asset\/[^/]+(\/.*)?$/);
		const tail = m && m[1] ? m[1] : "";
		const tailSegments = tail ? tail.split("/").filter(s => s.length > 0) : [];
		return ["org", organization_id, "fleet", fleet_id, "asset", asset_id, ...tailSegments];
	}

	public async DragOverFleet($event: any) {
		if ($event.dataTransfer.types.includes("asset_id")) {
			$event.preventDefault();
		}
	}

	public async DragHighlight($event: any, highlight: boolean) {
		const isAsset = $event.dataTransfer.types.includes("asset_id");
		if (highlight && isAsset) {
			$event.target.classList.add("dragover");
		} else {
			$event.target.classList.remove("dragover");
		}
	}

	public async DropOnFleet($event: any, destination_organization_id: number, destination_fleet_id: number | null, destination_fleet_name: string) {
		$event.preventDefault();
		const { organization_id, asset_id, asset_name } = JSON.parse($event.dataTransfer.getData("asset_id"));
		let postscript = "";
		const params = { fleet_id: destination_fleet_id };
		if (organization_id !== destination_organization_id) {
			postscript = "\nThe asset config will also be reset, because it is owned by the previous organization.";
			params["organization_id"] = destination_organization_id;
		}
		if (confirm("Are you sure you want to move the asset " + asset_name + " to the fleet " + (destination_fleet_name ?? "No fleet assigned") + "?\nThe OBCU will need to be updated." + postscript)) {
			await this.api.UpdateAssetAdmin(asset_id, params);
			this.router.navigate(["/admin/assets/org", destination_organization_id, "fleet", destination_fleet_id ?? "null", "asset", asset_id]);
			await this.UpdateTree();
		}
		$event.target.classList.remove("dragover");
	}

	private activeRouteComponent: any;
	private updateTreeSubscription: any;
	private assetSelectSubscription: any;
	public updateRouterOutlet(routecomponent?: any) {
		this.updateTreeSubscription?.unsubscribe();
		this.assetSelectSubscription?.unsubscribe();
		if (routecomponent) {
			this.activeRouteComponent = routecomponent;
		}
		if (this.activeRouteComponent) {
			if (this.activeRouteComponent.UpdateTree) {
				this.updateTreeSubscription = this.activeRouteComponent.UpdateTree.subscribe(async () => {
					await this.UpdateTree();
				});
			}
			if (this.activeRouteComponent.AssetSelected) {
				this.assetSelectSubscription = this.activeRouteComponent.AssetSelected.subscribe(async (asset: AssetRoutable) => {
					// Search through treenodes to find the asset and then route to it (can't route directly because we don't have orgid)
					for (const org of this.treenodes) {
						for (const fleet of org.fleets) {
							if (fleet.fleet_id === asset.fleet_id) {
								const found = fleet.assets.find(a => a.asset_id === asset.asset_id);
								if (found) {
									const el = document.querySelector(`div[data-orgid='${org.organization_id}']`);
									if (el) {
										el.classList.add("expanded");
										this.scrollNodeIntoView(el);
									}
									this.router.navigate(["/admin/assets/org", org.organization_id, "fleet", fleet.fleet_id, "asset", asset.asset_id]);
									return;
								}
							}
						}
					}
				});
			}
		}
	}

	searchFunc = (text$: Observable<string>) => {
		return text$.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			map(term => term.length < 2 ? []
				: this.searchables.filter(v => (v.name.toLowerCase().indexOf(term.toLowerCase()) > -1) || (v.serial && v.serial.toLowerCase().indexOf(term.toLowerCase()) > -1) || (v.obcu && v.obcu.toLowerCase().indexOf(term.toLowerCase()) > -1)).slice(0, 10))
		);
	}

	searchNav = (searchable) => {
		this.router.navigate([searchable.url]);
		const orgElement = document.querySelector(`div[data-orgid='${searchable.org}']`);
		if (orgElement) {
			orgElement.classList.add("expanded");
		}
		const nodeElement = document.querySelector(`a[data-nodeid='${searchable.id}']`);
		if (nodeElement) {
			this.scrollNodeIntoView(nodeElement);
		}
	}

}
